//BFSG
import * as React from 'react'
import styled from 'styled-components'
import theme from 'theme'
import Button from 'theme/atoms/Button'
import CheckRadio from 'theme/atoms/CheckRadio'
import Input from 'theme/atoms/Input'
import * as t from './types'
import useTranslations from 'hooks/useTranslations'
import withTranslations from './withTranslations'
import useNewsletterRegistrationForm from './useNewsletterRegistrationForm'
import EmailInvalid from './EmailInvalid'

export function NewsletterRegistrationForm(props: t.Props) {
  const t = useTranslations<'organisms-newsletter-registration-form'>()
  const $firstName = React.useRef<any>(null)
  const $lastName = React.useRef<any>(null)
  const $email = React.useRef<any>(null)
  const {
    channel,
    setChannel,
    firstName,
    setFirstName,
    lastName,
    setLastName,
    email,
    setEmail,
    completion,
    completionMode,
    onSubmit,
    error,
    setError
  } = useNewsletterRegistrationForm({ ...props, emailError: t('emailError') })
  const errorStateRef = React.useRef<any>(null)
  const successStateRef = React.useRef<any>(null)

  React.useEffect(() => {
    if (completion === 'success') {
      successStateRef.current?.focus()
    }
    if (completion === 'failure') {
      errorStateRef.current?.focus()
    }
  }, [completion])

  return (
    <Wrapper data-cy-ctx="organisms/NewsletterRegistrationForm">
      {completion === 'pending' && (
        <form
          data-cy-state="formular-shown"
          method="POST"
          onSubmit={onSubmit}
          noValidate
          encType="application/x-www-form-urlencoded"
        >
          <div className="newsletter-information">
            {props.headline && (
              <div className="headline" data-cy-state="show-headline">
                {props.headline}
              </div>
            )}
            {props.description && (
              <div className="description" data-cy-state="show-description">
                {props.description}
              </div>
            )}
          </div>
          <div className="input-fields">
            {props.showb2x && (
              <div
                className="channel-switch"
                data-cy-state="show-channel-switch"
              >
                <CheckRadio
                  data-cy-handle="b2b-switch"
                  variation="radio"
                  radiogroup="b2x"
                  label={t('newsletter_b2b')}
                  ariaLabel={t('newsletter_b2b') + ' selection button'} //TODO: bfsg-snippet
                  checked={channel === 'b2b'}
                  onClick={() => setChannel('b2b')}
                />
                <CheckRadio
                  data-cy-handle="b2c-switch"
                  variation="radio"
                  radiogroup="b2x"
                  label={t('newsletter_b2c')}
                  ariaLabel={t('newsletter_b2c') + ' selection button'} //TODO: bfsg-snippet
                  checked={channel === 'b2c'}
                  onClick={() => setChannel('b2c')}
                />
              </div>
            )}
            {props.showFirstname && (
              <Input
                type="text"
                inputRef={$firstName}
                data-cy-state="show-firstname"
                placeholder={t.asText($firstName, 'firstname')}
                aria-label={t.asText($firstName, 'firstname')}
                data-cy-handle="firstname-input"
                value={firstName}
                onChange={(value) => {
                  setFirstName(value)
                }}
                error={''}
              />
            )}
            {props.showLastname && (
              <Input
                type="text"
                inputRef={$lastName}
                data-cy-state="show-lastname"
                placeholder={t.asText($lastName, 'lastname')}
                aria-label={t.asText($lastName, 'firstname')}
                data-cy-handle="lastname-input"
                value={lastName}
                onChange={(value) => {
                  setLastName(value)
                }}
                error={''}
              />
            )}
            <Input
              type="email"
              inputRef={$email}
              placeholder={t.asText($email, 'email')}
              aria-label={t.asText($email, 'email')}
              data-cy-handle="email-input"
              value={email}
              onChange={(value) => {
                setEmail(value)
                setError('')
              }}
              error={
                error && (
                  <EmailInvalid
                    data-cy-state="show-email-invalid-error"
                    errorText={error}
                  />
                )
              }
            />
          </div>

          <Button
            variation="special"
            data-cy-handle="submit-button"
            label={props.submitButtonText}
            submit
          ></Button>
          <div>{props.newsletterConditionsText}</div>
        </form>
      )}
      {completion === 'success' && (
        <div
          ref={successStateRef}
          className="success-screen"
          data-cy-state="success-shown"
          role="status"
          aria-live="polite"
          tabIndex={-1}
        >
          <div className="headline">{props.successHeadline}</div>
          <div className="description">{props.successText}</div>
          {completionMode === 'MAIL_TRIGGER' && (
            <div
              data-cy-state="success-shown-mail-triggered"
              className="description"
            >
              {props.successTextMailTriggered}
            </div>
          )}
        </div>
      )}
      {completion === 'failure' && (
        <div
          className="failure-screen"
          data-cy-state="failure-shown"
          ref={errorStateRef}
          role="alert"
          aria-live="assertive"
          tabIndex={-1}
        >
          <div className="headline">{props.failureHeadline}</div>
          <div className="description">{props.failureText}</div>
        </div>
      )}
    </Wrapper>
  )
}

export default withTranslations(NewsletterRegistrationForm)

const Wrapper = styled.div`
  ${theme.ty('rc-base')}
  position: relative;
  padding: ${theme.spacing('xs')};
  > .spinner-overlay {
    position: absolute;
    width: 100%;
    height: 100%;
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: ${theme.zIndex.theme_organisms_newsletterregistrationform};
    /* background: rgba(0, 0, 0, 0); */
  }
  > form {
    padding: ${theme.spacing('xs')};
    display: flex;
    flex-direction: column;
    row-gap: 10px;
    > .newsletter-information {
      margin-bottom: ${theme.spacing('xs')};
      > .headline {
        ${theme.ty('rc-3xl')}
        margin-bottom: ${theme.spacing('xs')};
      }
      > .description {
      }
    }
    > .input-fields {
      display: flex;
      flex-direction: column;
      row-gap: 10px;
      margin-bottom: ${theme.spacing('xs')};
      > .channel-switch {
        display: flex;
        gap: ${theme.spacing('xs')};
      }
    }
    > .newsletter-condition {
      display: flex;
      > .condition-link {
        margin-left: ${theme.spacing('xxs')};
        color: ${theme.colors.primary};
      }
    }
    > .newsletter-condition {
      display: flex;
    }
  }

  > .success-screen,
  > .failure-screen {
    padding: ${theme.spacing('xs')};
    > .headline {
      ${theme.ty('rc-3xl')}
      margin-bottom: ${theme.spacing('xs')};
    }
    > .description {
    }
  }
`
